import { useEffect, useRef, useState } from "react";
import { X, Lock, Loader2 } from "lucide-react";
import toast from "react-hot-toast";
import { cn } from "@/utils/cn";
import { AddMeetingForm } from "./AddMeetingForm";
import { useUpdateSignOffAction, useDraftMeetingHistory } from "@/services/draftCalendar";
import type { FiscalYearResponse, DraftMeetingResponse } from "@/types/draftCalendar";

type FlyoutTab = "details" | "history";

interface MeetingFlyoutProps {
  meetingId: number;
  fiscalYear: FiscalYearResponse | undefined;
  businesses: Record<string, string[]>;
  meetingTypes: Record<string, Record<string, string[]>>;
  attendeeLevels: string[];
  locations: string[];
  duDeclinedReasons?: string[];
  isViewOnly: boolean;
  lockedBy: string | null;
  canEdit: boolean;
  /** Whether the current user can copy this meeting to a new one (may differ from canEdit) */
  canCopyToNew?: boolean;
  isBusinessApprover?: boolean;
  canEditSignOffStatus?: boolean;
  signOffStatus?: string | null;
  /** Pre-populated data for Copy To New (only used when meetingId === 0) */
  copyFromData?: DraftMeetingResponse;
  /** Whether the current user can push a meeting to Summit */
  canPushToSummit?: boolean;
  onEdit: () => void;
  onSaved: () => void;
  onClose: () => void;
  /** Called when user clicks Copy To New — page handles fetching data and opening create form */
  onCopyToNew?: () => void;
  /** Called when user clicks Push To Summit — page handles confirmation + mutation */
  onPushToSummit?: () => void;
}

/**
 * Right-side flyout panel for viewing / editing a draft meeting.
 * Wraps AddMeetingForm with a title bar, lock indicator, and Edit/Cancel controls.
 * Mirrors Blazor CreateDraftEvent popup pattern.
 */
export function MeetingFlyout({
  meetingId,
  fiscalYear,
  businesses,
  meetingTypes,
  attendeeLevels,
  locations,
  duDeclinedReasons = [],
  isViewOnly,
  lockedBy,
  canEdit,
  canCopyToNew = canEdit,
  isBusinessApprover = false,
  canEditSignOffStatus = false,
  signOffStatus = null,
  copyFromData,
  canPushToSummit = false,
  onEdit,
  onSaved,
  onClose,
  onCopyToNew,
  onPushToSummit
}: MeetingFlyoutProps) {
  const submitSignOff = useUpdateSignOffAction();
  const isNew = meetingId === 0;
  const [isSaving, setIsSaving] = useState(false);

  const [activeTab, setActiveTab] = useState<FlyoutTab>("details");

  // Snap back to Details whenever editing starts (mirrors CreateSoftHoldDialog)
  useEffect(() => {
    if (!isViewOnly) setActiveTab("details");
  }, [isViewOnly]);

  // Load history for the History tab — only fetches when tab is active (enabled=false until then)
  const { data: historyItems = [], isPending: isMeetingPending } = useDraftMeetingHistory(
    meetingId,
    !isNew && activeTab === "history"
  );

  async function handleSubmitSignOff() {
    if (signOffStatus?.toLowerCase() === "signed off") {
      toast("This draft meeting has already been signed off.");
      return;
    }
    try {
      const count = await submitSignOff.mutateAsync({
        draftIds: [meetingId],
        signOffAction: "Signed Off",
      });
      if (count > 0) {
        toast.success("Sign-off submitted successfully.");
        onSaved();
      } else {
        toast.error("Failed to submit sign-off. Please try again.");
      }
    } catch {
      toast.error("Failed to submit sign-off. Please try again.");
    }
  }

  const title = isNew ? "Add Meeting" : isViewOnly ? "Meeting Details" : "Edit Meeting";
  const panelRef = useRef<HTMLDivElement>(null);

  // Move focus into the panel when it opens.
  useEffect(() => {
    const firstFocusable = panelRef.current?.querySelector<HTMLElement>(
      'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    firstFocusable?.focus();
  }, []);

  // Trap focus within the dialog panel.
  function handlePanelKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
    if (e.key === "Escape") {
      onClose();
      return;
    }
    if (e.key !== "Tab") return;
    const focusables = Array.from(
      panelRef.current?.querySelectorAll<HTMLElement>(
        'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      ) ?? []
    );
    if (focusables.length === 0) return;
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  return (
    <>
      {/* Backdrop — presentational, not interactive for AT */}
      <div
        className="fixed inset-0 z-50 bg-black/30"
        onClick={onClose}
        aria-hidden="true"
      />
      {/* Dialog panel */}
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="meeting-flyout-title"
        tabIndex={-1}
        className="fixed inset-y-0 right-0 z-50 ml-auto flex h-full w-full max-w-3xl flex-col border-l border-slate-300 bg-white shadow-xl"
        onKeyDown={handlePanelKeyDown}
      >
        {/* Header — black, mirrors CreateSoftHoldDialog */}
        <div className="flex items-center justify-between bg-black px-4 py-3">
          <div className="flex items-center gap-3">
            <h2 id="meeting-flyout-title" className="text-base font-semibold text-white">
              {title}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close dialog"
            className="text-white hover:text-slate-300"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Lock banner */}
        {lockedBy && isViewOnly && !isNew && (
          <div className="flex items-center gap-2 border-b border-amber-200 bg-amber-50 px-4 py-2 text-xs text-amber-900">
            <Lock className="h-3.5 w-3.5 flex-shrink-0" />
            Currently being edited by <strong className="ml-1">{lockedBy}</strong>
          </div>
        )}

        {/* Tabs — shown for existing meetings, equal-width like Blazor .equal-width-tabs */}
        {!isNew && (
          <div
            role="tablist"
            aria-label="Meeting sections"
            className="flex items-end gap-0 bg-white px-4 pt-4"
          >
            <button
              role="tab"
              type="button"
              aria-selected={activeTab === "details"}
              onClick={() => setActiveTab("details")}
              style={{ width: "50%" }}
              className={cn(
                "relative -mb-px px-5 py-2.5 text-sm font-semibold transition-colors focus:outline-none",
                activeTab === "details"
                  ? "border border-b-white border-slate-200 bg-black text-white"
                  : "border border-transparent text-slate-600 hover:text-slate-900"
              )}
            >
              Meeting Details
            </button>
            <button
              role="tab"
              type="button"
              aria-selected={activeTab === "history"}
              onClick={() => { if (isViewOnly) setActiveTab("history"); }}
              disabled={!isViewOnly}
              title={!isViewOnly ? "History is unavailable while editing." : undefined}
              style={{ width: "50%" }}
              className={cn(
                "relative -mb-px px-5 py-2.5 text-sm font-semibold transition-colors focus:outline-none",
                activeTab === "history"
                  ? "border border-b-white border-slate-200 bg-black text-white"
                  : "border border-transparent text-slate-600 hover:text-slate-900",
                !isViewOnly && "cursor-not-allowed opacity-40"
              )}
            >
              History
            </button>
          </div>
        )}

        {/* History tab panel */}
        {!isNew && activeTab === "history" ? (
          <div
            role="tabpanel"
            aria-label="Meeting history"
            className="flex-1 overflow-y-auto px-4 py-4"
          >
            {isMeetingPending ? (
              <div className="flex items-center justify-center py-10 text-sm text-slate-500">
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Loading history...
              </div>
            ) : historyItems.length === 0 ? (
              <div className="py-6 text-sm text-slate-500">No history available.</div>
            ) : (
              <ul className="space-y-0 border border-slate-200">
                {historyItems.map((item, index) => (
                  <li
                    key={`${item.softHoldId}-${index}`}
                    className={cn(
                      "px-3 py-3 text-sm",
                      index % 2 === 0 ? "bg-white" : "bg-slate-50"
                    )}
                  >
                    <span className="text-slate-800" style={{ fontSize: 14 }}>
                      {item.displayMessage}
                    </span>
                    <br />
                    <span className="text-slate-500" style={{ fontSize: 10 }}>
                      {item.updatedBy}{item.updatedBy && item.createdDate ? ", " : ""}{item.createdDate ?? ""}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        ) : (
          /* Details tab / form */
          <div className="flex flex-1 overflow-hidden">
          <AddMeetingForm
            meetingId={meetingId}
            fiscalYear={fiscalYear}
            businesses={businesses}
            meetingTypes={meetingTypes}
            attendeeLevels={attendeeLevels}
            locations={locations}
            duDeclinedReasons={duDeclinedReasons}
            isViewOnly={isViewOnly}
            isBusinessApprover={isBusinessApprover}
            canEditSignOffStatus={canEditSignOffStatus}
            copyFromData={copyFromData}
            onSaved={onSaved}
            onCancel={onClose}
            onSavingChange={setIsSaving}
          />
          </div>
        )}

        {/* Footer actions — mirrors CreateSoftHoldDialog footer */}
        <div className="flex justify-end gap-2 border-t border-slate-200 px-4 py-3">
          {isViewOnly && !isNew && canCopyToNew && onCopyToNew && (
            <button
              type="button"
              onClick={onCopyToNew}
              className="rounded-none border border-slate-900 bg-slate-900 px-4 py-2 text-sm text-white hover:bg-slate-800"
            >
              Copy To New
            </button>
          )}
          {isViewOnly && !isNew && canPushToSummit && onPushToSummit && (
            <button
              type="button"
              onClick={onPushToSummit}
              className="rounded-none border border-slate-300 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50"
            >
              Push To Summit
            </button>
          )}
          {isViewOnly && !isNew && canEdit && (
            <button
              type="button"
              onClick={onEdit}
              className="rounded-none border border-slate-300 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50"
            >
              Edit
            </button>
          )}
          {isViewOnly && isBusinessApprover && signOffStatus?.toLowerCase() === "pending" && (
            <button
              type="button"
              onClick={() => void handleSubmitSignOff()}
              disabled={submitSignOff.isPending}
              className={cn(
                "rounded-none border border-slate-900 bg-slate-900 px-4 py-2 text-sm text-white",
                "hover:bg-slate-800 disabled:opacity-60"
              )}
            >
              {submitSignOff.isPending ? "Submitting\u2026" : "Submit Sign Off"}
            </button>
          )}
          <button
            type="button"
            onClick={onClose}
            className="rounded-none border border-slate-300 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50"
          >
            {isViewOnly ? "Cancel" : isNew ? "Cancel" : "Back"}
          </button>
          {!isViewOnly && (
            <button
              type="submit"
              form="meeting-form"
              disabled={isSaving}
              className={cn(
                "inline-flex items-center gap-2 rounded-none border border-slate-900 bg-slate-900 px-4 py-2 text-sm text-white",
                "hover:bg-slate-800 disabled:opacity-60 disabled:cursor-not-allowed"
              )}
            >
              {isSaving ? "Saving\u2026" : isNew ? "Save" : "Update"}
            </button>
          )}
        </div>
      </div>
    </>
  );
}
